import { QueryCache, QueryClient, QueryClientProvider, useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { toast } from "react-toastify";

import { RESOURCE_NOT_FOUND } from "utils/Api";
import i18n from "utils/i18n";

import type { DefaultError, DefinedInitialDataOptions, DefinedUseQueryResult, UndefinedInitialDataOptions, UseQueryResult } from "@tanstack/react-query";
import type { ReactNode } from "react";

type QueryKeyBase = {
  component: string;
};

type LocalQueryKey = QueryKeyBase & {
  data: Record<string, unknown> | string;
};

type BackendQueryKey = QueryKeyBase & {
  baseUrl: string;
  endpoint: string;
  params?: Record<string, unknown>;
};

type PermanentBackendQueryKey = BackendQueryKey & {
  permanent: boolean;
};

enum ORIGIN {
  Local = 1,
  Backend,
  PermanentBackend
}

interface TypedQueryKeyBase<T> {
  origin: ORIGIN;
  dataType?: T; // dummy just to reference the type that we want to store for that key, never actually gets a value.
}

type TypedLocalQueryKey<T> = TypedQueryKeyBase<T> & LocalQueryKey;
export type TypedBackendQueryKey<T> = TypedQueryKeyBase<T> & BackendQueryKey;
export type TypedPermanentBackendQueryKey<T> = TypedQueryKeyBase<T> & PermanentBackendQueryKey;

type MissingQueryKeyType = "Missing Query Key Type";
type MandatoryQueryKeyType<T> = [T] extends [never] ? MissingQueryKeyType : unknown extends T ? MissingQueryKeyType : undefined extends T ? MissingQueryKeyType : T;
type NotAny<T> = 0 extends 1 & T ? never : T;

export type MandatoryLocalTypedQueryKey<T> = [T] extends MissingQueryKeyType ? MandatoryLocalTypedQueryKey<"Typed Query Key"> : TypedLocalQueryKey<T>;
export type MandatoryBackendTypedQueryKey<T> = [T] extends MissingQueryKeyType ? MandatoryBackendTypedQueryKey<"Typed Query Key"> : TypedBackendQueryKey<T>;
export type MandatoryPermanentBackendTypedQueryKey<T> = [T] extends MissingQueryKeyType
  ? MandatoryPermanentBackendTypedQueryKey<"Typed Query Key">
  : TypedPermanentBackendQueryKey<T>;

export type MandatoryTypedQueryKey<T> = MandatoryLocalTypedQueryKey<T> | MandatoryBackendTypedQueryKey<T> | MandatoryPermanentBackendTypedQueryKey<T>;

export function localQueryKey<T>(queryKey: LocalQueryKey): TypedLocalQueryKey<MandatoryQueryKeyType<T>> {
  return { origin: ORIGIN.Local, ...queryKey };
}

export function backendQueryKey<T>(queryKey: BackendQueryKey): TypedBackendQueryKey<MandatoryQueryKeyType<T>> {
  return { origin: ORIGIN.Backend, ...queryKey };
}

export function permanentBackendQueryKey<T>(queryKey: BackendQueryKey): TypedPermanentBackendQueryKey<MandatoryQueryKeyType<T>> {
  return { origin: ORIGIN.PermanentBackend, permanent: true, ...queryKey };
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.meta?.skipErrorToast || error.message.includes(RESOURCE_NOT_FOUND)) return;

      toast.error(error.message || i18n.t("v8_something_went_wrong", "Something went wrong, please contact the administrator"));
    }
  }),
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000 * 60 * 24, // Our sessions are 24h long
      refetchOnReconnect: false,
      gcTime: 0 // Once a key is not used by any mounted component we clean it up directly by default, local/permanent keys override that and are removed on demande only
    }
  }
});

export const TypedQueryClientProvider = ({ children }: { children: ReactNode }) => <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;

function getQueryData<T>(queryKey: MandatoryTypedQueryKey<T>) {
  return queryClient.getQueryData<T, ReadonlyArray<MandatoryTypedQueryKey<T>>>([queryKey]);
}

function setQueryData<T>(queryKey: MandatoryTypedQueryKey<T>, updater: NotAny<T> | ((previousData: NotAny<T> | undefined) => NotAny<T> | undefined)) {
  if ([ORIGIN.Local, ORIGIN.PermanentBackend].includes(queryKey.origin)) queryClient.setQueryDefaults([queryKey], { gcTime: Infinity });

  queryClient.setQueryData<NotAny<T>, ReadonlyArray<MandatoryTypedQueryKey<T>>>([queryKey], updater);
}

function invalidateQueries<T>(queryKey: MandatoryTypedQueryKey<T>) {
  queryClient.invalidateQueries({ queryKey: [queryKey] });
}

function removeQueries<T>(queryKey: MandatoryTypedQueryKey<T>) {
  queryClient.removeQueries({ queryKey: [queryKey] });
}

function resetQueries<T>(queryKey: MandatoryTypedQueryKey<T>) {
  queryClient.resetQueries({ queryKey: [queryKey] });
}

function clear() {
  queryClient.clear();
}

export const typedQueryClient = { getQueryData, setQueryData, invalidateQueries, removeQueries, resetQueries, clear };

type TypeOfLocalTypedQueryKey<K> = K extends TypedLocalQueryKey<infer T> ? T : never;
export type TypeOfBackendTypedQueryKey<K> = K extends TypedBackendQueryKey<infer T> ? T : never;
export type TypeOfPermanentBackendTypedQueryKey<K> = K extends TypedPermanentBackendQueryKey<infer T> ? T : never;

type TypeOfTypedQueryKey<K> = TypeOfLocalTypedQueryKey<K> | TypeOfBackendTypedQueryKey<K> | TypeOfPermanentBackendTypedQueryKey<K>;

export type UndefinedInitialBackendDataTypedOptions<K, R> = Omit<
  UndefinedInitialDataOptions<TypeOfBackendTypedQueryKey<K>, DefaultError, R, ReadonlyArray<K>>,
  "queryKey" | "queryFn" | "gcTime" | "retry"
> & {
  queryKey: K;
  queryFn: (queryKey: K) => TypeOfBackendTypedQueryKey<K> | Promise<TypeOfBackendTypedQueryKey<K>>;
  refetchOnDependencies?: unknown[];
};

export type UndefinedInitialPermanentBackendDataTypedOptions<K, R> = Omit<
  UndefinedInitialDataOptions<TypeOfPermanentBackendTypedQueryKey<K>, DefaultError, R, ReadonlyArray<K>>,
  "queryKey" | "queryFn" | "gcTime" | "retry"
> & {
  queryKey: K;
  queryFn: (queryKey: K) => TypeOfPermanentBackendTypedQueryKey<K> | Promise<TypeOfPermanentBackendTypedQueryKey<K>>;
  refetchOnDependencies?: unknown[];
};

type DefinedInitialLocalDataTypedOptions<K> = Omit<
  DefinedInitialDataOptions<TypeOfLocalTypedQueryKey<K> | undefined, DefaultError, TypeOfLocalTypedQueryKey<K>, ReadonlyArray<K>>,
  "queryKey" | "queryFn" | "gcTime" | "retry" | "select"
> & {
  queryKey: K;
  queryFn?: never;
  select?: never;
  refetchOnDependencies?: unknown[];
};

type DefinedInitialBackendDataTypedOptions<K, R> = Omit<
  DefinedInitialDataOptions<TypeOfBackendTypedQueryKey<K>, DefaultError, R, ReadonlyArray<K>>,
  "queryKey" | "queryFn" | "gcTime" | "retry"
> & {
  queryKey: K;
  queryFn: (queryKey: K) => TypeOfBackendTypedQueryKey<K> | Promise<TypeOfBackendTypedQueryKey<K>>;
  refetchOnDependencies?: unknown[];
};

type DefinedInitialPermanentBackendDataTypedOptions<K, R> = Omit<
  DefinedInitialDataOptions<TypeOfPermanentBackendTypedQueryKey<K>, DefaultError, R, ReadonlyArray<K>>,
  "queryKey" | "queryFn" | "gcTime" | "retry"
> & {
  queryKey: K;
  queryFn: (queryKey: K) => TypeOfPermanentBackendTypedQueryKey<K> | Promise<TypeOfPermanentBackendTypedQueryKey<K>>;
  refetchOnDependencies?: unknown[];
};

type DataTypedOptions<K, R> =
  | UndefinedInitialBackendDataTypedOptions<K, R>
  | UndefinedInitialPermanentBackendDataTypedOptions<K, R>
  | DefinedInitialLocalDataTypedOptions<K>
  | DefinedInitialBackendDataTypedOptions<K, R>
  | DefinedInitialPermanentBackendDataTypedOptions<K, R>;

function isLocalOptions<K extends MandatoryTypedQueryKey<unknown>, R>(options: DataTypedOptions<K, R>): options is DefinedInitialLocalDataTypedOptions<K> {
  return options.queryKey.origin === ORIGIN.Local;
}

function isBackendOptions<K extends MandatoryTypedQueryKey<unknown>, R>(
  options: DataTypedOptions<K, R>
): options is UndefinedInitialBackendDataTypedOptions<K, R> | DefinedInitialBackendDataTypedOptions<K, R> {
  return options.queryKey.origin === ORIGIN.Backend;
}

export function useTypedQuery<K extends MandatoryPermanentBackendTypedQueryKey<unknown>, R = TypeOfPermanentBackendTypedQueryKey<K>>(
  options: DefinedInitialPermanentBackendDataTypedOptions<K, R>
): DefinedUseQueryResult<R, DefaultError>;

export function useTypedQuery<K extends MandatoryLocalTypedQueryKey<unknown>, R = TypeOfLocalTypedQueryKey<K>>(
  options: DefinedInitialLocalDataTypedOptions<K>
): DefinedUseQueryResult<R, DefaultError>;

export function useTypedQuery<K extends MandatoryBackendTypedQueryKey<unknown>, R = TypeOfBackendTypedQueryKey<K>>(
  options: DefinedInitialBackendDataTypedOptions<K, R>
): DefinedUseQueryResult<R, DefaultError>;

export function useTypedQuery<K extends MandatoryPermanentBackendTypedQueryKey<unknown>, R = TypeOfPermanentBackendTypedQueryKey<K>>(
  options: UndefinedInitialPermanentBackendDataTypedOptions<K, R>
): UseQueryResult<R, DefaultError>;

export function useTypedQuery<K extends MandatoryBackendTypedQueryKey<unknown>, R = TypeOfBackendTypedQueryKey<K>>(
  options: UndefinedInitialBackendDataTypedOptions<K, R>
): UseQueryResult<R, DefaultError>;

export function useTypedQuery<K extends MandatoryTypedQueryKey<unknown>, R = TypeOfTypedQueryKey<K>>(options: DataTypedOptions<K, R>) {
  let query;

  if (isLocalOptions(options)) {
    query = useQuery<TypeOfLocalTypedQueryKey<K> | undefined, DefaultError, TypeOfLocalTypedQueryKey<K>, ReadonlyArray<K>>({
      ...options,
      queryKey: [options.queryKey],
      queryFn: () => undefined,
      gcTime: Infinity
    });
  } else if (isBackendOptions(options)) {
    query = useQuery<TypeOfBackendTypedQueryKey<K>, DefaultError, R, ReadonlyArray<K>>({
      ...options,
      queryKey: [options.queryKey],
      queryFn: ({ queryKey }) => options.queryFn(queryKey[0])
    });
  } else {
    query = useQuery<TypeOfPermanentBackendTypedQueryKey<K>, DefaultError, R, ReadonlyArray<K>>({
      ...options,
      queryKey: [options.queryKey],
      queryFn: ({ queryKey }) => options.queryFn(queryKey[0]),
      gcTime: Infinity
    });
  }

  useEffect(() => {
    if (!query.isFetching && options.refetchOnDependencies && Object.values(options.refetchOnDependencies).every(Boolean)) query.refetch();
  }, options.refetchOnDependencies ?? []);

  return query;
}

interface TypedMutationOptions<P, R> {
  mutationFn: (payload: P) => Promise<R>;
  onSuccess: (payload: P, response: R) => void;
  onError?: (error: DefaultError) => void;
}

export function useTypedMutation<P, R>(options: TypedMutationOptions<P, R>) {
  return useMutation<R, DefaultError, P>({
    mutationFn: options.mutationFn,
    onSuccess: (response, payload) => options.onSuccess(payload, response),
    onError: options.onError ?? (error => toast.error(error.message))
  });
}
